"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import { OpticsFigure } from "@/components/tools/physics-waves/optics-figure";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-waves/format";
import {
  imageNature,
  type OpticsKind,
  type OpticsSolution,
  type OpticsTarget,
  opticalPower,
  solveOptics,
} from "@/lib/tool-kit/physics-waves/optics";
import {
  findUnit,
  fromBase,
  toBase,
  type UnitDef,
} from "@/lib/tool-kit/physics-waves/units";

/** English strings. A translated content file overrides these through `ui`. */
export const THIN_LENS_EQUATION_CALCULATOR_STRINGS = {
  kindLabel: "Lens or mirror?",
  kinds: { lens: "Thin lens", mirror: "Spherical mirror" },
  targetLabel: "What do you want to find?",
  targets: {
    image: "Image distance",
    focal: "Focal length",
    object: "Object distance",
  },
  typeLabel: "Type",
  types: { converging: "Converging", diverging: "Diverging" },
  typeHint: {
    lens: "Converging = convex lens; diverging = concave lens.",
    mirror: "Converging = concave mirror; diverging = convex mirror.",
  },
  imageTypeLabel: "The image is",
  imageTypes: { real: "Real (positive dᵢ)", virtual: "Virtual (negative dᵢ)" },
  focalInput: "Focal length (size, without a sign)",
  objectInput: "Object distance (dₒ)",
  imageInput: "Image distance (size, without a sign)",
  heightInput: "Object height (hₒ)",
  heightHint: "Used for the image height and the drawing.",
  unitFor: "Unit for {name}",
  showIn: "Show results in",
  sigFigs: "Round to this many significant figures",
  tryLabel: "Try an example",
  presets: {
    camera: "Camera lens, 50 mm at 2 m",
    magnifier: "Magnifying glass",
    peephole: "Door peephole (diverging lens)",
    shaving: "Shaving mirror (concave)",
    carMirror: "Car mirror (convex)",
  },
  results: "Results",
  enterAll: "Enter a number in every box to see the result.",
  errObject: "The object distance must be above zero.",
  errFocal:
    "The object is at the focal point, so the rays leave parallel and no image forms (it is at infinity). Move the object slightly.",
  errZero:
    "These distances cannot go together: they would need an infinite focal length or object distance.",
  errInvalid: "Every distance must be a number above zero.",
  imageDistance: "Image distance (dᵢ)",
  focalLength: "Focal length (f)",
  objectDistance: "Object distance (dₒ)",
  magnification: "Magnification (m)",
  imageHeight: "Image height (hᵢ)",
  power: "Optical power (1/f)",
  nature: "The image is",
  location: "The image forms {side}",
  natureLine: "{real}, {upright}, {size}",
  real: "real",
  virtual: "virtual",
  upright: "upright",
  inverted: "inverted",
  magnified: "magnified",
  reduced: "reduced",
  same: "the same size",
  sideLensReal: "on the far side of the lens",
  sideLensVirtual: "on the same side as the object",
  sideMirrorReal: "in front of the mirror",
  sideMirrorVirtual: "behind the mirror",
  convergingWord: "converging",
  divergingWord: "diverging",
  working: "The working",
  stepImage: "1/dᵢ = 1/f − 1/dₒ = 1/{f} − 1/{do} → dᵢ = {di}",
  stepFocal: "1/f = 1/dₒ + 1/dᵢ = 1/{do} + 1/{di} → f = {f}",
  stepObject: "1/dₒ = 1/f − 1/dᵢ = 1/{f} − 1/{di} → dₒ = {do}",
  stepMagnification: "m = −dᵢ ÷ dₒ = −({di}) ÷ {do} = {m}",
  stepHeight: "hᵢ = m × hₒ = {m} × {ho} = {hi}",
  convention:
    "Sign convention (real is positive): f is positive for a converging lens or mirror and negative for a diverging one; dₒ is positive for a real object; dᵢ is positive for a real image and negative for a virtual one; a negative magnification means the image is inverted.",
  objectLabel: "Object",
  imageLabel: "Image",
  imageTooFar: "The image is too far away to draw",
  figureLabel: "Ray diagram with the three principal rays",
  figureDescription:
    "An object arrow, the lens or mirror, and its image, with three principal rays: one parallel to the axis, one through the centre and one through the focal point.",
} as const;

type Strings = typeof THIN_LENS_EQUATION_CALCULATOR_STRINGS;
type PresetKey = keyof Strings["presets"];

const LENGTH_UNITS: UnitDef[] = [
  { id: "mm", symbol: "mm", exp: -3 },
  { id: "cm", symbol: "cm", exp: -2 },
  { id: "m", symbol: "m", exp: 0 },
];
const SIG_CHOICES = ["3", "4", "5"] as const;

interface Entry {
  value: string;
  unit: string;
}

interface State {
  kind: OpticsKind;
  target: OpticsTarget;
  converging: boolean;
  real: boolean;
  f: Entry;
  dObject: Entry;
  dImage: Entry;
  height: Entry;
}

const INITIAL: State = {
  kind: "lens",
  target: "image",
  converging: true,
  real: true,
  f: { value: "10", unit: "cm" },
  dObject: { value: "25", unit: "cm" },
  dImage: { value: "16.67", unit: "cm" },
  height: { value: "5", unit: "cm" },
};

const PRESETS: Record<PresetKey, Partial<State>> = {
  camera: {
    kind: "lens",
    target: "image",
    converging: true,
    f: { value: "50", unit: "mm" },
    dObject: { value: "2", unit: "m" },
    height: { value: "1.8", unit: "m" },
  },
  magnifier: {
    kind: "lens",
    target: "image",
    converging: true,
    f: { value: "10", unit: "cm" },
    dObject: { value: "6", unit: "cm" },
    height: { value: "2", unit: "cm" },
  },
  peephole: {
    kind: "lens",
    target: "image",
    converging: false,
    f: { value: "10", unit: "cm" },
    dObject: { value: "25", unit: "cm" },
    height: { value: "5", unit: "cm" },
  },
  shaving: {
    kind: "mirror",
    target: "image",
    converging: true,
    f: { value: "20", unit: "cm" },
    dObject: { value: "10", unit: "cm" },
    height: { value: "6", unit: "cm" },
  },
  carMirror: {
    kind: "mirror",
    target: "image",
    converging: false,
    f: { value: "50", unit: "cm" },
    dObject: { value: "300", unit: "cm" },
    height: { value: "150", unit: "cm" },
  },
};

function readMetres(entry: Entry): number | null {
  const value = parseNumberInput(entry.value);
  return value === null
    ? null
    : toBase(value, findUnit(LENGTH_UNITS, entry.unit));
}

function errorMessage(error: string, strings: Strings): string {
  if (error === "need-positive-object") {
    return strings.errObject;
  }
  if (error === "at-focal-point") {
    return strings.errFocal;
  }
  return error === "zero-denominator" ? strings.errZero : strings.errInvalid;
}

function Results({
  solution,
  kind,
  target,
  height,
  outUnit,
  strings,
  fmt,
  sig,
}: {
  solution: OpticsSolution;
  kind: OpticsKind;
  target: OpticsTarget;
  height: number | null;
  outUnit: UnitDef;
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const nature = imageNature(solution);
  const length = (metres: number) =>
    `${formatSig(fmt, fromBase(metres, outUnit), sig)} ${outUnit.symbol}`;
  const sides = {
    lens: { real: strings.sideLensReal, virtual: strings.sideLensVirtual },
    mirror: {
      real: strings.sideMirrorReal,
      virtual: strings.sideMirrorVirtual,
    },
  };
  const side = sides[kind][nature.real ? "real" : "virtual"];
  const natureText = t(strings.natureLine, {
    real: nature.real ? strings.real : strings.virtual,
    upright: nature.upright ? strings.upright : strings.inverted,
    size: strings[nature.size === "same" ? "same" : nature.size],
  });
  const m = formatSig(fmt, solution.magnification, sig);
  const text = {
    f: length(solution.f),
    do: length(solution.dObject),
    di: length(solution.dImage),
    m,
    ho: height === null ? "" : length(height),
    hi: height === null ? "" : length(solution.magnification * height),
  };
  const steps = {
    image: strings.stepImage,
    focal: strings.stepFocal,
    object: strings.stepObject,
  };
  const lines = [t(steps[target], text), t(strings.stepMagnification, text)];
  if (height !== null) {
    lines.push(t(strings.stepHeight, text));
  }
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis={target === "image"}
          label={strings.imageDistance}
          value={length(solution.dImage)}
        />
        <StatCard
          emphasis={target === "focal"}
          label={strings.focalLength}
          value={length(solution.f)}
        />
        <StatCard
          emphasis={target === "object"}
          label={strings.objectDistance}
          value={length(solution.dObject)}
        />
        <StatCard label={strings.magnification} value={m} />
        {height === null ? null : (
          <StatCard
            label={strings.imageHeight}
            value={length(solution.magnification * height)}
          />
        )}
        <StatCard
          label={strings.power}
          value={`${formatSig(fmt, opticalPower(solution.f), sig)} D`}
        />
      </StatGrid>
      <div className="rounded-lg border border-border bg-card p-4">
        <p className="text-muted-foreground text-xs">{strings.nature}</p>
        <p className="font-semibold text-foreground">{natureText}</p>
        <p className="text-muted-foreground text-sm">
          {t(strings.location, { side })}
        </p>
      </div>
      <OpticsFigure
        kind={kind}
        objectHeight={height ?? solution.dObject * 0.2}
        solution={solution}
        strings={strings}
      />
      <WorkingSteps heading={strings.working} lines={lines} />
      <p className="text-muted-foreground text-xs">{strings.convention}</p>
    </>
  );
}

function TypePills({
  state,
  onChange,
  strings,
}: {
  state: State;
  onChange: (patch: Partial<State>) => void;
  strings: Strings;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {state.target === "focal" ? null : (
        <PillGroup
          label={strings.typeLabel}
          name="tl-type"
          onChange={(value) => onChange({ converging: value === "converging" })}
          options={[
            { value: "converging", label: strings.types.converging },
            { value: "diverging", label: strings.types.diverging },
          ]}
          value={state.converging ? "converging" : "diverging"}
        />
      )}
      {state.target === "image" ? null : (
        <PillGroup
          label={strings.imageTypeLabel}
          name="tl-image-type"
          onChange={(value) => onChange({ real: value === "real" })}
          options={[
            { value: "real", label: strings.imageTypes.real },
            { value: "virtual", label: strings.imageTypes.virtual },
          ]}
          value={state.real ? "real" : "virtual"}
        />
      )}
    </div>
  );
}

export default function ThinLensEquationCalculatorWidget() {
  const strings = useToolUi(THIN_LENS_EQUATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [state, setState] = useState(INITIAL);
  const [outUnitId, setOutUnitId] = useState("cm");
  const [sigText, setSigText] = useState<(typeof SIG_CHOICES)[number]>("4");
  const set = (patch: Partial<State>) =>
    setState((current) => ({ ...current, ...patch }));
  const sig = Number(sigText);
  const outUnit = findUnit(LENGTH_UNITS, outUnitId);
  const { target } = state;

  const f = readMetres(state.f);
  const dObject = readMetres(state.dObject);
  const dImage = readMetres(state.dImage);
  const height = readMetres(state.height);
  const known = {
    f: f === null ? undefined : (state.converging ? 1 : -1) * f,
    dObject: dObject ?? undefined,
    dImage: dImage === null ? undefined : (state.real ? 1 : -1) * dImage,
  };
  const needed = {
    image: [f, dObject],
    focal: [dObject, dImage],
    object: [f, dImage],
  }[target];
  const missing = needed.some((x) => x === null);
  const result = missing ? null : solveOptics(target, known);

  const field = (
    key: "f" | "dObject" | "dImage" | "height",
    label: string,
    hint?: string
  ) => (
    <QuantityField
      hint={hint}
      id={`tl-${key}`}
      label={label}
      onChange={(value) => set({ [key]: { ...state[key], value } })}
      onUnitChange={(unit) => set({ [key]: { ...state[key], unit } })}
      unit={state[key].unit}
      unitLabel={t(strings.unitFor, { name: label })}
      units={LENGTH_UNITS}
      value={state[key].value}
    />
  );

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.kindLabel}
          name="tl-kind"
          onChange={(kind) => set({ kind })}
          options={(Object.keys(strings.kinds) as OpticsKind[]).map(
            (value) => ({ value, label: strings.kinds[value] })
          )}
          value={state.kind}
        />
        <PillGroup
          label={strings.targetLabel}
          name="tl-target"
          onChange={(next) => set({ target: next })}
          options={(Object.keys(strings.targets) as OpticsTarget[]).map(
            (value) => ({ value, label: strings.targets[value] })
          )}
          value={state.target}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {(Object.keys(PRESETS) as PresetKey[]).map((key) => (
          <button
            className={BUTTON_SMALL}
            key={key}
            onClick={() => set({ ...PRESETS[key], target: "image" })}
            type="button"
          >
            {strings.presets[key]}
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {target === "focal" ? null : field("f", strings.focalInput)}
        {target === "object" ? null : field("dObject", strings.objectInput)}
        {target === "image" ? null : field("dImage", strings.imageInput)}
        {field("height", strings.heightInput, strings.heightHint)}
      </div>
      <TypePills onChange={set} state={state} strings={strings} />
      <p className="text-muted-foreground text-xs">
        {strings.typeHint[state.kind]}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="tl-out"
          label={strings.showIn}
          onChange={setOutUnitId}
          options={LENGTH_UNITS.map((u) => ({ value: u.id, label: u.symbol }))}
          value={outUnitId}
        />
        <SelectField
          id="tl-sig"
          label={strings.sigFigs}
          onChange={setSigText}
          options={SIG_CHOICES.map((value) => ({ value, label: value }))}
          value={sigText}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {missing ? <Notice>{strings.enterAll}</Notice> : null}
        {result !== null && !result.ok ? (
          <ErrorNotice>{errorMessage(result.error, strings)}</ErrorNotice>
        ) : null}
        {result?.ok ? (
          <Results
            fmt={fmt}
            height={height}
            kind={state.kind}
            outUnit={outUnit}
            sig={sig}
            solution={result.solution}
            strings={strings}
            target={target}
          />
        ) : null}
      </section>
    </>
  );
}
